// Rotalar, rota editörü, sihirbaz, talepler, ısı haritası (C09–C12, C18–C20): Zod girdileri ve yanıt tipleri (API ve konsol aynı tipi kullanır).
// Yolcu tarafı talep tipleri (RequestInput, RequestItem) `api/passenger.ts`'tedir; burada yalnız firma (Control) tarafı.
import { z } from 'zod';
import {
  REQUEST_STATUSES,
  REQUEST_TYPES,
  type Direction,
  type PassengerKind,
  type RequestStatus,
  type RequestType,
} from '../enums';
import type { TripDisplay } from '../state';
import type { Paged } from './common';

const Hhmm = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Saat SS:DD biçiminde olmalı (ör. 07:15)');
const Day = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Tarih YYYY-AA-GG biçiminde olmalı');

// ───────── rotalar (C09) ─────────

export const ROUTE_VERSION_STATUSES = ['DRAFT', 'PUBLISHED', 'ARCHIVED'] as const;
export type RouteVersionStatus = (typeof ROUTE_VERSION_STATUSES)[number];
export const ROUTE_VERSION_STATUS_LABELS: Record<RouteVersionStatus, string> = {
  DRAFT: 'Taslak',
  PUBLISHED: 'Yürürlükte',
  ARCHIVED: 'Arşiv',
};
export const ROUTE_STATUS_LABELS: Record<string, string> = { ACTIVE: 'Aktif', INACTIVE: 'Pasif' };

export const STOP_KINDS = ['START', 'PICKUP', 'DESTINATION'] as const;
export type StopKind = (typeof STOP_KINDS)[number];
export const STOP_KIND_LABELS: Record<StopKind, string> = {
  START: 'Başlangıç',
  PICKUP: 'Durak',
  DESTINATION: 'Varış',
};

/** 1 = Pazartesi … 7 = Pazar */
export const WEEKDAY_SHORT_LABELS = ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'] as const;

/** "Pzt–Cum", "Pzt, Çar, Cum", "Her gün" */
export function daysLabel(days: number[]): string {
  const d = [...new Set(days)].filter((x) => x >= 1 && x <= 7).sort((a, b) => a - b);
  if (!d.length) return '—';
  if (d.length === 7) return 'Her gün';
  const contiguous = d.every((x, i) => i === 0 || x === d[i - 1]! + 1);
  if (contiguous && d.length >= 3) return `${WEEKDAY_SHORT_LABELS[d[0]! - 1]}–${WEEKDAY_SHORT_LABELS[d.at(-1)! - 1]}`;
  return d.map((x) => WEEKDAY_SHORT_LABELS[x - 1]).join(', ');
}

export const RouteListQuery = z.object({
  status: z.enum(['ACTIVE', 'INACTIVE', 'all']).default('ACTIVE'),
  institutionId: z.string().max(64).optional(),
  direction: z.enum(['OUTBOUND', 'INBOUND']).optional(),
  q: z.string().trim().max(80).optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(50),
});
export type RouteListQuery = z.infer<typeof RouteListQuery>;

export interface RouteRef {
  id: string;
  name: string;
}

export interface RouteRow {
  id: string;
  code: string;
  name: string;
  status: string;
  institutionId: string;
  institution: string;
  directions: Direction[];
  departures: Partial<Record<Direction, string>>;
  days: number[];
  stopCount: number;
  activeVersion: { id: string; version: number; publishedAt: string | null } | null;
  hasDraft: boolean;
  vehicle: { id: string; plate: string; capacity: number } | null;
  driver: RouteRef | null;
  guide: RouteRef | null;
  /** aktif versiyonun duraklarına atanmış (etkin) yolcu sayısı (yönlerin büyüğü) */
  assigned: number;
  /** atanan / araç kapasitesi (%) */
  occupancyPct: number | null;
  /** son 14 gün tamamlanan seferlerde gecikme eşiği altında kalanların oranı (%) */
  onTimePct: number | null;
  distanceKm: number | null;
  durationMin: number | null;
}

export interface RouteListResult extends Paged<RouteRow> {
  counts: { active: number; inactive: number; drafts: number };
  institutions: RouteRef[];
}

// ───────── rota detayı (C10) ─────────

export interface RouteStopItem {
  id: string;
  order: number;
  name: string;
  address: string | null;
  lat: number;
  lng: number;
  kind: StopKind;
  /** gidiş yönünde sefer başından planlanan saniye */
  offsetS: number;
  assigned: Record<Direction, number>;
}

export interface RouteScheduleItem {
  id: string;
  direction: Direction;
  daysOfWeek: number[];
  departureTime: string;
  arrivalTime: string | null;
  vehicle: { id: string; plate: string; capacity: number };
  driver: RouteRef;
  guide: RouteRef | null;
  validFrom: string;
  validTo: string | null;
  active: boolean;
}

export interface RouteVersionItem {
  id: string;
  version: number;
  status: RouteVersionStatus;
  effectiveFrom: string | null;
  publishedAt: string | null;
  createdAt: string;
  changeNote: string | null;
  createdBy: string | null;
  distanceM: number;
  durationS: number;
  stopCount: number;
}

export interface RouteVersionDetail extends RouteVersionItem {
  routeId: string;
  polyline: string;
  stops: RouteStopItem[];
}

export interface RouteRecentTrip {
  id: string;
  date: string;
  direction: Direction;
  display: TripDisplay;
  plannedStartAt: string;
  actualStartAt: string | null;
  delayMin: number;
  plate: string;
  driver: string;
  boarded: number;
}

export interface RouteDetail {
  id: string;
  code: string;
  name: string;
  status: string;
  institutionId: string;
  institution: string;
  contract: { id: string; number: string } | null;
  version: RouteVersionDetail | null;
  draft: RouteVersionItem | null;
  schedules: RouteScheduleItem[];
  versions: RouteVersionItem[];
  recentTrips: RouteRecentTrip[];
  metrics: {
    assigned: number;
    capacity: number | null;
    occupancyPct: number | null;
    onTimePct: number | null;
    trips14d: number;
    avgDelayMin: number | null;
  };
}

export const RouteUpdateInput = z
  .object({
    name: z.string().trim().min(3, 'Rota adı en az 3 harf olmalı.').max(80).optional(),
    status: z.enum(['ACTIVE', 'INACTIVE']).optional(),
  })
  .refine((v) => v.name !== undefined || v.status !== undefined, { message: 'Değiştirilecek bir alan seçin.' });
export type RouteUpdateInput = z.infer<typeof RouteUpdateInput>;

// ───────── rota editörü (C11) ─────────

export const EditorStopInput = z.object({
  /** kaynak durak (yürürlükteki versiyondan); yeni durakta yok */
  id: z.string().max(64).optional(),
  name: z.string().trim().min(2, 'Durak adı en az 2 harf olmalı.').max(80, 'Durak adı en fazla 80 harf olabilir.'),
  address: z.string().trim().max(200).nullish(),
  lat: z.number().min(35.5, 'Durak Türkiye sınırları dışında.').max(42.5, 'Durak Türkiye sınırları dışında.'),
  lng: z.number().min(25.5, 'Durak Türkiye sınırları dışında.').max(45, 'Durak Türkiye sınırları dışında.'),
});
export type EditorStopInput = z.infer<typeof EditorStopInput>;

const StopList = z
  .array(EditorStopInput)
  .min(2, 'Rota en az iki duraktan (başlangıç ve varış) oluşur.')
  .max(40, 'Bir rotada en fazla 40 durak olabilir.');

export const RoutePreviewInput = z.object({
  /** düzenlenen rota: değişmeyen ardışık durak çiftlerinde yürürlükteki yol geometrisi korunur */
  routeId: z.string().max(64).optional(),
  stops: StopList,
  /** sihirbaz maliyet tahmini için (isteğe bağlı) */
  contractId: z.string().max(64).optional(),
  vehicleId: z.string().max(64).optional(),
  daysOfWeek: z.array(z.number().int().min(1).max(7)).max(7).optional(),
  directions: z
    .array(z.enum(['OUTBOUND', 'INBOUND']))
    .max(2)
    .optional(),
});
export type RoutePreviewInput = z.infer<typeof RoutePreviewInput>;

export interface RouteCostEstimate {
  pricingModel: string;
  tripsPerMonth: number;
  kmPerMonth: number;
  /** aylık tahmini hakediş (kuruş) */
  monthlyKurus: number;
  basis: string;
}

export interface RoutePreview {
  polyline: string;
  distanceM: number;
  durationS: number;
  /** her durak için gidiş yönünde sefer başından saniye */
  offsets: number[];
  legs: { distanceM: number; durationS: number; reused: boolean }[];
  provider: string;
  estimate: RouteCostEstimate | null;
}

export const RouteDraftInput = z.object({
  stops: StopList,
  changeNote: z.string().trim().max(200).optional(),
});
export type RouteDraftInput = z.infer<typeof RouteDraftInput>;

export const AFFECT_REASONS = ['MOVED', 'REMOVED', 'TIME'] as const;
export type AffectReason = (typeof AFFECT_REASONS)[number];
export const AFFECT_REASON_LABELS: Record<AffectReason, string> = {
  MOVED: 'Durağı taşındı',
  REMOVED: 'Durağı kaldırıldı',
  TIME: 'Saati değişti',
};

export interface AffectedPassenger {
  passengerId: string;
  name: string;
  kind: PassengerKind;
  direction: Direction;
  reason: AffectReason;
  fromStop: string;
  toStop: string;
  /** taşınma mesafesi (m) */
  movedM: number | null;
  /** planlanan saat farkı (dk, + geç) */
  timeDeltaMin: number | null;
  /** bildirim alacak hesap var mı (çalışan kendi hesabı / veli) */
  notifiable: boolean;
}

export interface PublishImpact {
  draftVersion: number;
  currentVersion: number | null;
  affected: AffectedPassenger[];
  counts: { moved: number; removed: number; time: number; passengers: number; notifiable: number };
  stops: { added: string[]; removed: string[]; moved: string[]; renamed: string[] };
  /** yeni versiyona taşınacak, henüz başlamamış seferler */
  futureTrips: number;
  /** devam eden sefer varsa yayın beklemeli */
  inProgressTrips: number;
  distanceDeltaM: number | null;
  durationDeltaS: number | null;
}

export const RoutePublishInput = z.object({
  changeNote: z.string().trim().max(200).optional(),
  notify: z.boolean().default(true),
});
export type RoutePublishInput = z.infer<typeof RoutePublishInput>;

export interface RoutePublishResult {
  versionId: string;
  version: number;
  moved: number;
  notified: number;
  tripsUpdated: number;
}

// ───────── rota oluşturma sihirbazı (C12) ─────────

export const RouteCreateInput = z
  .object({
    contractId: z.string().min(1, 'Sözleşme seçin.').max(64),
    code: z
      .string()
      .trim()
      .toUpperCase()
      .regex(/^[A-Z0-9][A-Z0-9-]{1,9}$/, 'Rota kodu 2–10 harf/rakam olmalı (ör. R40).'),
    name: z.string().trim().min(3, 'Rota adı en az 3 harf olmalı.').max(80),
    directions: z
      .array(z.enum(['OUTBOUND', 'INBOUND']))
      .min(1, 'En az bir yön seçin.')
      .max(2),
    daysOfWeek: z.array(z.number().int().min(1).max(7)).min(1, 'En az bir gün seçin.').max(7),
    times: z.object({ OUTBOUND: Hhmm.optional(), INBOUND: Hhmm.optional() }),
    stops: StopList,
    vehicleId: z.string().min(1, 'Araç seçin.').max(64),
    driverId: z.string().min(1, 'Şoför seçin.').max(64),
    guideId: z.string().max(64).nullish(),
    validFrom: Day.optional(),
  })
  .refine((v) => v.directions.every((d) => !!v.times[d]), {
    message: 'Seçtiğiniz her yön için kalkış saati girin.',
    path: ['times'],
  });
export type RouteCreateInput = z.infer<typeof RouteCreateInput>;

/** C11 tarife satırı düzenleme: kalkış saati, günler, araç, şoför, rehber (yön değişmez; diğer yön için yeni satır). */
export const ScheduleUpdateInput = z.object({
  departureTime: Hhmm,
  daysOfWeek: z.array(z.number().int().min(1).max(7)).min(1, 'En az bir gün seçin.').max(7),
  vehicleId: z.string().min(1, 'Araç seçin.').max(64),
  driverId: z.string().min(1, 'Şoför seçin.').max(64),
  guideId: z.string().max(64).nullish(),
});
export type ScheduleUpdateInput = z.infer<typeof ScheduleUpdateInput>;

/** C11 tarifeye yeni satır (ör. eksik dönüş yönü ya da ikinci sabah seferi). */
export const ScheduleCreateInput = ScheduleUpdateInput.extend({
  direction: z.enum(['OUTBOUND', 'INBOUND']),
});
export type ScheduleCreateInput = z.infer<typeof ScheduleCreateInput>;

/** Tarife değişikliğinin seferlere yansıması (yanıt) */
export interface ScheduleChangeResult {
  schedule: RouteScheduleItem;
  /** başlamamış gelecek seferlerden güncellenen */
  tripsUpdated: number;
  /** yeni üretilen sefer */
  tripsCreated: number;
  /** artık tarifede olmayan günlerin başlamamış seferleri (iptal) */
  tripsCancelled: number;
}

export interface RouteWizardOptions {
  contracts: {
    id: string;
    number: string;
    institutionId: string;
    institution: string;
    institutionKind: string | null;
    pricingModel: string;
    unitPrice: number;
    endsAt: string;
  }[];
  vehicles: { id: string; plate: string; capacity: number; label: string; routes: string[] }[];
  drivers: { id: string; name: string; routes: string[] }[];
  /** okul modülü kapalıysa boş */
  guides: { id: string; name: string; routes: string[] }[];
  nextCode: string;
  schoolEnabled: boolean;
}

// ───────── talepler (C18/C19) ─────────

export const REQUEST_STATUS_FILTERS = ['open', ...REQUEST_STATUSES, 'all'] as const;
export type RequestStatusFilter = (typeof REQUEST_STATUS_FILTERS)[number];

/** Açık talep: karar bekleyen. */
export const OPEN_REQUEST_STATUSES: RequestStatus[] = ['NEW', 'REVIEWING', 'INSTITUTION_APPROVAL'];

/** Onayda durak ataması yapan talep tipleri (geçici talepler yalnız kayıt altına alınır). */
export const ASSIGNING_REQUEST_TYPES: RequestType[] = [
  'ADDRESS_CHANGE',
  'ROUTE_REQUEST',
  'DIFFERENT_LINE',
  'NEW_SERVICE',
  'JOIN_SERVICE',
];

export const RequestInboxQuery = z.object({
  status: z.enum(REQUEST_STATUS_FILTERS).default('open'),
  type: z.enum(REQUEST_TYPES).optional(),
  institutionId: z.string().max(64).optional(),
  q: z.string().trim().max(80).optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(50),
});
export type RequestInboxQuery = z.infer<typeof RequestInboxQuery>;

export interface RequestInboxRow {
  id: string;
  number: string;
  type: RequestType;
  status: RequestStatus;
  summary: string;
  institutionId: string | null;
  institution: string | null;
  /** veri minimizasyonu: ad + soyadın baş harfi */
  requester: string;
  passengerKind: PassengerKind | null;
  area: string | null;
  hasLocation: boolean;
  suggestedStop: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface RequestInboxPage extends Paged<RequestInboxRow> {
  counts: Record<RequestStatus, number> & { open: number; all: number };
  institutions: RouteRef[];
}

export interface NearbyStop {
  stopId: string;
  name: string;
  lat: number;
  lng: number;
  routeId: string;
  routeCode: string;
  routeName: string;
  /** yürüme mesafesi tahmini (kuş uçuşu × 1,25) */
  walkingM: number;
  /** gidiş seferinde planlanan durak saati */
  plannedTime: string | null;
  assigned: number;
  capacity: number | null;
  sameInstitution: boolean;
}

export interface RequestTimelineEvent {
  at: string;
  status: RequestStatus | null;
  title: string;
  detail: string | null;
  by: string | null;
}

export interface RequestDetail extends RequestInboxRow {
  steps: { status: RequestStatus; label: string; done: boolean; current: boolean }[];
  decisionNote: string | null;
  lat: number | null;
  lng: number | null;
  address: string | null;
  destination: string | null;
  time: string | null;
  date: string | null;
  until: string | null;
  note: string | null;
  /** yolcunun bugünkü hattı ve durağı (varsa) */
  current: { routeId: string; routeCode: string; routeName: string; stopName: string; stopId: string } | null;
  requesterDetail: {
    name: string;
    kind: PassengerKind | null;
    group: string | null;
    phoneMasked: string | null;
    /** öğrenci talebinde veli */
    guardian: string | null;
  };
  nearbyStops: NearbyStop[];
  routes: { id: string; code: string; name: string; polyline: string }[];
  timeline: RequestTimelineEvent[];
  /** onayda durak ataması yapılır mı */
  assigns: boolean;
}

export const RequestDecisionInput = z.discriminatedUnion('action', [
  z.object({
    action: z.literal('APPROVE'),
    stopId: z.string().max(64).optional(),
    directions: z
      .array(z.enum(['OUTBOUND', 'INBOUND']))
      .min(1)
      .max(2)
      .default(['OUTBOUND', 'INBOUND']),
    note: z.string().trim().max(300).optional(),
  }),
  z.object({
    action: z.literal('REJECT'),
    reason: z.string().trim().min(5, 'Ret gerekçesini en az 5 harfle yazın.').max(300),
  }),
  z.object({
    action: z.literal('ASK_INFO'),
    message: z.string().trim().min(5, 'Sorunuzu en az 5 harfle yazın.').max(300),
  }),
  z.object({ action: z.literal('REVIEW') }),
  z.object({ action: z.literal('FORWARD'), note: z.string().trim().max(300).optional() }),
]);
export type RequestDecisionInput = z.infer<typeof RequestDecisionInput>;

export const REQUEST_ACTION_LABELS: Record<RequestDecisionInput['action'], string> = {
  APPROVE: 'Onaylandı',
  REJECT: 'Reddedildi',
  ASK_INFO: 'Bilgi istendi',
  REVIEW: 'İncelemeye alındı',
  FORWARD: 'Kurum onayına gönderildi',
};

// ───────── talep ısı haritası (C20, AI_OPTIMIZATION) ─────────

export const RequestHeatmapQuery = z.object({
  days: z.coerce
    .number()
    .int()
    .refine((d) => [7, 30, 90, 365].includes(d), 'Dönem 7, 30, 90 veya 365 gün olmalı.')
    .default(90),
  institutionId: z.string().max(64).optional(),
  type: z.enum(REQUEST_TYPES).optional(),
  status: z.enum(['open', 'all']).default('all'),
});
export type RequestHeatmapQuery = z.infer<typeof RequestHeatmapQuery>;

export interface DemandCluster {
  id: string;
  area: string;
  district: string | null;
  lat: number;
  lng: number;
  count: number;
  open: number;
  types: Partial<Record<RequestType, number>>;
  /** en yakın mevcut durak ve hattı (kuş uçuşu) */
  nearestStop: { name: string; routeCode: string; distanceM: number } | null;
}

export interface RequestHeatmap {
  days: number;
  total: number;
  withoutLocation: number;
  points: { lat: number; lng: number; weight: number }[];
  clusters: DemandCluster[];
  institutions: RouteRef[];
}
